// src/components/Questionnaires/LearningOnlineCoursesPartE.js

import React, {useEffect, useState} from 'react';
import {
    Card,
    Form,
    Radio,
    Checkbox,
    Input,
    Button,
    Alert,
    Typography,
    message,
    Spin,
    Space,
    Divider,
} from 'antd';
import {PlusOutlined, MinusCircleOutlined} from '@ant-design/icons';
import moment from 'moment';
import {InputNumber} from 'antd'; // Import InputNumber
import "./LearningOnlineCoursesPartE.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const LearningOnlineCoursesPartE = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(
                    `${server_url}/questionnaires/learning-online-courses-partE/eligibility`,
                    {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    }
                );

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(
                        `${server_url}/questionnaires/learning-online-courses-partE/latest`,
                        {
                            method: 'GET',
                            headers: {Authorization: `Bearer ${accessToken}`},
                        }
                    );

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Helper function to convert number fields from strings to numbers
    const processValues = (values) => {
        const processedValues = {...values};

        // Convert total_hours to number
        if (processedValues.total_hours !== undefined) {
            processedValues.total_hours = Number(processedValues.total_hours);
        }

        // Convert courses.course_hours to number
        if (processedValues.courses && Array.isArray(processedValues.courses)) {
            processedValues.courses = processedValues.courses.map((course) => ({
                ...course,
                course_hours: course.course_hours
                    ? Number(course.course_hours)
                    : 0,
            }));
        }

        // Convert summary_table.summary_course_hours to number and ensure summary_course_completed is boolean
        if (
            processedValues.summary_table &&
            Array.isArray(processedValues.summary_table)
        ) {
            processedValues.summary_table = processedValues.summary_table.map(
                (summary) => ({
                    ...summary,
                    summary_course_hours: summary.summary_course_hours
                        ? Number(summary.summary_course_hours)
                        : 0,
                    summary_course_completed: !!summary.summary_course_completed,
                })
            );
        }

        return processedValues;
    };

    // Recursive function to render nested objects and arrays
    const renderValue = (value) => {
        if (typeof value === 'string' || typeof value === 'number') {
            return value;
        } else if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        } else if (Array.isArray(value)) {
            return (
                <ul>
                    {value.map((item, idx) => (
                        <li key={idx}>{renderValue(item)}</li>
                    ))}
                </ul>
            );
        } else if (typeof value === 'object' && value !== null) {
            return (
                <ul>
                    {Object.entries(value).map(([k, v], idx) => (
                        <li key={idx}>
                            <Text strong>{formatQuestion(k)}: </Text>
                            {renderValue(v)}
                        </li>
                    ))}
                </ul>
            );
        } else {
            return String(value);
        }
    };

    // Helper functions to format questions and answers for display
    const formatQuestion = (key) => {
        const questionMap = {
            courses: 'Courses',
            time_management:
                'How did you manage your time to complete these online courses?',
            skills_acquired:
                'What are the key skills or knowledge you gained from these courses?',
            most_impactful_course:
                'Which course did you find most impactful and why?',
            application_of_learning:
                'Have you applied the knowledge or skills from any of these courses in real-life situations? Provide examples.',
            preferred_learning_platforms:
                'Which platform(s) did you use for these courses (e.g., Coursera, Udemy, etc.)?',
            future_learning_goals:
                'Are there any online courses you plan to take in the future? If yes, specify the subject or platform.',
            challenges_faced:
                'What were the main challenges you faced while taking these online courses?',
            overcoming_challenges:
                'How did you overcome these challenges?',
            overall_experience_rating:
                'How would you rate your overall experience with online learning?',
            recommendations_for_beginners:
                'What recommendations would you give to others who are starting with online courses?',
            suggestions_for_improvement:
                'How can these online courses be improved to enhance the learning experience?',
            summary_table: 'Summary Table',
            total_hours: 'Total Hours',
        };
        return questionMap[key] || key;
    };

    const formatAnswer = (key, value) => {
        if (key === 'courses' || key === 'summary_table') {
            return renderValue(value);
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        return value;
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            // Process values to ensure correct data types
            const processedValues = processValues(values);
            console.log('Submitting processed values:', processedValues); // Debugging

            const response = await fetch(
                `${server_url}/questionnaires/learning-online-courses-partE/submit`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        form_data: {
                            answers: processedValues, // Ensure this matches backend expectation
                        },
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                // Throw the entire error data for detailed handling
                throw errorData;
            }

            const data = await response.json();
            message.success('Learning Online Courses Part E submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(
                `Next eligible date: ${moment(
                    data.next_eligible_date
                ).format('YYYY-MM-DD')}`
            );
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Learning Online Courses Part E:', err);
            // Display the error message from the server
            if (err.detail && Array.isArray(err.detail)) {
                const errorMessages = err.detail
                    .map((e) => e.msg)
                    .filter((msg) => msg) // Filter out undefined messages
                    .join(', ');
                message.error(errorMessages || 'Submission failed.');
            } else if (err.detail) {
                message.error(err.detail);
            } else if (err.errors && Array.isArray(err.errors)) {
                const errorMessages = err.errors
                    .map((e) => e.msg)
                    .join(', ');
                message.error(errorMessages);
            } else {
                message.error('An error occurred while submitting the form.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="pe-container">
                <h2 className="pe-header">PART E</h2>
                <h3 className="pe-subheader">Learning through Online Courses</h3>
                <table className="pe-table">
                    <thead>
                    <tr className="pe-thead-tr">
                        <th className="pe-th">Online Course</th>
                        <th className="pe-th">Course</th>
                        <th className="pe-th">Number of Hours</th>
                        <th className="pe-th">Completed (✓)</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td className="pe-td">a)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">b)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">c)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">d)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">e)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">f)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">g)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">h)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">i)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="pe-td">j)</td>
                        <td className="pe-td"><input type="text" className="pe-input-text"/></td>
                        <td className="pe-td"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-td"><input type="checkbox" className="pe-checkbox"/></td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr className="pe-tfoot-tr">
                        <td className="pe-tfoot-cell" colSpan="2">TOTAL HOURS</td>
                        <td className="pe-tfoot-cell"><input type="number" className="pe-input-number"/></td>
                        <td className="pe-tfoot-cell"></td>
                    </tr>
                    </tfoot>
                </table>
            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? 'success' : 'warning'}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* General Information *!/*/}
            {/*        <Title level={4}>General Information</Title>*/}

            {/*        <Title level={5}>Course Details</Title>*/}
            {/*        /!* Fixed Five Summary Entries *!/*/}
            {/*        <div>*/}
            {/*            {[1, 2, 3, 4, 5].map((num) => (*/}
            {/*                <div*/}
            {/*                    key={`summary_${num}`}*/}
            {/*                    style={{*/}
            {/*                        display: 'flex',*/}
            {/*                        justifyContent: 'space-between',*/}
            {/*                        marginBottom: '16px',*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <Form.Item*/}
            {/*                        label={`Course ${num} Name`}*/}
            {/*                        name={`summary_course_name_${num}`}*/}
            {/*                        rules={[*/}
            {/*                            {*/}
            {/*                                required: true,*/}
            {/*                                message: 'Please enter the course name.',*/}
            {/*                            },*/}
            {/*                        ]}*/}
            {/*                        style={{flex: 2, marginRight: '8px'}}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Course Name"/>*/}
            {/*                    </Form.Item>*/}

            {/*                    <Form.Item*/}
            {/*                        label={`Course ${num} Hours`}*/}
            {/*                        name={`summary_course_hours_${num}`}*/}
            {/*                        rules={[*/}
            {/*                            {*/}
            {/*                                required: true,*/}
            {/*                                message: 'Please enter the number of hours.',*/}
            {/*                            },*/}
            {/*                            // {*/}
            {/*                            //     type: 'number',*/}
            {/*                            //     min: 0,*/}
            {/*                            //     message:*/}
            {/*                            //         'Course hours must be a non-negative number.',*/}
            {/*                            // },*/}
            {/*                        ]}*/}
            {/*                        style={{flex: 1, marginRight: '8px'}}*/}
            {/*                    >*/}
            {/*                        <Input*/}
            {/*                            type="number"*/}
            {/*                            min="0"*/}
            {/*                            placeholder="Number of Hours"*/}
            {/*                        />*/}
            {/*                    </Form.Item>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}


            {/*        <Divider/>*/}


            {/*        /!* Time Management *!/*/}
            {/*        <Title level={4}>Time Management</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="How did you manage your time to complete these online courses?"*/}
            {/*            name="time_management"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please describe your time management strategies.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Describe how you managed your time."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Skills Acquired *!/*/}
            {/*        <Title level={4}>Skills Acquired</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="What are the key skills or knowledge you gained from these courses?"*/}
            {/*            name="skills_acquired"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please list the skills or knowledge gained.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="List the key skills or knowledge gained."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Course Impact *!/*/}
            {/*        <Title level={4}>Course Impact</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Which course did you find most impactful and why?"*/}
            {/*            name="most_impactful_course"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please describe the most impactful course and why.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Describe the most impactful course and why."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Application of Learning *!/*/}
            {/*        <Title level={4}>Application of Learning</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Have you applied the knowledge or skills from any of these courses in real-life situations? Provide examples."*/}
            {/*            name="application_of_learning"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please provide examples of application.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Provide examples of how you applied the knowledge or skills."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Preferred Learning Platforms *!/*/}
            {/*        <Title level={4}>Preferred Learning Platforms</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Which platform(s) did you use for these courses (e.g., Coursera, Udemy, etc.)?"*/}
            {/*            name="preferred_learning_platforms"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message: 'Please list the platforms used.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Coursera, Udemy, edX"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Future Learning Goals *!/*/}
            {/*        <Title level={4}>Future Learning Goals</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Are there any online courses you plan to take in the future? If yes, specify the subject or platform."*/}
            {/*            name="future_learning_goals"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please specify your future learning goals.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Specify the subject or platform for future courses."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Divider/>*/}

            {/*        /!* Reflection and Feedback *!/*/}
            {/*        <Title level={4}>Reflection and Feedback</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What were the main challenges you faced while taking these online courses?"*/}
            {/*            name="challenges_faced"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please describe the main challenges.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Describe the main challenges faced."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How did you overcome these challenges?"*/}
            {/*            name="overcoming_challenges"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please describe how you overcame the challenges.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Describe how you overcame the challenges."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How would you rate your overall experience with online learning?"*/}
            {/*            name="overall_experience_rating"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message: 'Please select a rating.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Excellent">Excellent</Radio>*/}
            {/*                <Radio value="Good">Good</Radio>*/}
            {/*                <Radio value="Average">Average</Radio>*/}
            {/*                <Radio value="Below Average">Below Average</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What recommendations would you give to others who are starting with online courses?"*/}
            {/*            name="recommendations_for_beginners"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please provide your recommendations.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Provide your recommendations for beginners."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How can these online courses be improved to enhance the learning experience?"*/}
            {/*            name="suggestions_for_improvement"*/}
            {/*            rules={[*/}
            {/*                {*/}
            {/*                    required: true,*/}
            {/*                    message:*/}
            {/*                        'Please provide suggestions for improvement.',*/}
            {/*                },*/}
            {/*            ]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="Provide suggestions for course improvement."*/}
            {/*            />*/}
            {/*        </Form.Item>*/}


            {/*        <Divider/>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button*/}
            {/*                type="primary"*/}
            {/*                htmlType="submit"*/}
            {/*                className="bg-yellow-500 hover:bg-yellow-600 text-black"*/}
            {/*                loading={isSubmitting}*/}
            {/*            >*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please*/}
            {/*        come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    <Text strong>Submission Date:</Text>
                    <br/>
                    <Text>
                        {moment(latestSubmission.submission_date).format(
                            'YYYY-MM-DD HH:mm:ss'
                        )}
                    </Text>
                    <Divider/>
                    {Object.entries(latestSubmission.qna).map(
                        ([key, value], index) => (
                            <div key={index} style={{marginBottom: 16}}>
                                <Text strong>{formatQuestion(key)}</Text>
                                <br/>
                                <Text>{formatAnswer(key, value)}</Text>
                            </div>
                        )
                    )}
                </Card>
            )}
        </Card>
    );
}
export default LearningOnlineCoursesPartE;
